<template>
  <header id="setupHeader"
          class="setup-header"
          :class="[{ 'prevent-click': preventHeaderClick }, { 'application-progress': showMobileProgressBar }]">
    <!-- Screen Reader text -->
    <b-row>
      <b-col>
        <h1 class="sr-only">
          {{ ariaHeader }}
        </h1>
      </b-col>
    </b-row>

    <b-row no-gutters
           align-v="center"
           align-h="between">
      <b-col cols="auto">
        <button v-if="showMenu"
                id="sidebarMenuButton"
                class="btn button--icon"
                type="button"
                :aria-label="$t('open_menu')"
                aria-haspopup="menu"
                aria-controls="sidebarMenu"
                aria-expanded="false"
                @click="openSidebar">
          <inline-svg class="icon--hamburger"
                      src="HamburgerIcon">
          </inline-svg>
        </button>

        <button v-else-if="showBack"
                id="backButton"
                class="btn button--icon"
                type="button"
                :aria-label="$t('go_back')"
                @click="goBack">
          <div id="backButtonImage"></div>
        </button>

        <div v-else
             class="placeholder">
        </div>
      </b-col>
      <b-col cols="auto">
        <brand-logo></brand-logo>
      </b-col>
      <b-col cols="auto">
        <div class="placeholder"></div>
      </b-col>
    </b-row>

    <b-row v-show="showMobileProgressBar"
           align="center">
      <b-col>
        <application-progress></application-progress>
      </b-col>
    </b-row>
  </header>
</template>

<script>
// Components
import ApplicationProgress from '../../application/elements/ApplicationProgress.vue'
import BrandLogo from '../elements/BrandLogo.vue'
import InlineSvg from './InlineSvg.vue'

// Constants
import { MenuButtonEnum } from '../../../constants'

// Libraries
import { mapState } from 'vuex'

// Services
import { EventBus } from '../../../services/eventBusService'

// Utilities
import isEmpty from '../../../utility/isEmpty'
import Router from '../../../utility/routerUtil'

export default {
  name: 'setup-header',

  components: {
    ApplicationProgress,
    BrandLogo,
    InlineSvg
  },

  mounted () {
    EventBus.$on('StopBackNav', this.onStopBackNav)
  },

  beforeDestroy () {
    EventBus.$off('StopBackNav', this.onStopBackNav)
  },

  data () {
    return {
      stopBackNav: false
    }
  },

  computed: {
    showMenu () {
      return this.header.buttonType === MenuButtonEnum.menu && !this.sidebarOpen
    },

    showBack () {
      return this.header.buttonType === MenuButtonEnum.back
    },

    showMobileProgressBar () {
      // This is only for the mobile version
      const isApplicationFlow = Router.isRouteInFlow('Application', this.$route)
      const scanIdMode = document.location.search.includes('scanIdDataUuid') || !isEmpty(this.$route.query.scanIdDataUuid)
      const pageHasSection = !isEmpty(this.$route.meta?.section) && this.$route.meta?.section !== 'NONE'

      return isApplicationFlow && (this.isMobileBrowser || !this.wideViewport) && !scanIdMode && pageHasSection
    },

    ...mapState('display', ['ariaHeader', 'header', 'preventHeaderClick', 'sidebarOpen', 'isMobileBrowser', 'wideViewport']),
    ...mapState('payment', ['disbursementAmount'])
  },

  methods: {
    async goBack () {
      EventBus.$emit('HeaderBackButtonClick')

      await this.$nextTick()

      if (this.stopBackNav) {
        return
      }

      if (this.$route.path.indexOf('/AddPaymentMethod') > 0) { // If the user is in the payment flow without a saved card, gets to manage payment, this will prevent a loop hitting back button
        this.$router.push({ name: 'MakePayment' }) // The user could have come from MakePayment or ReviewPayment, for now just default them back to MakePayment
      } else {
        window.history.length > 1 ? this.$router.go(-1) : this.$router.push({ name: 'Login' })
      }
    },

    openSidebar () {
      this.$store.commit('display/toggleSidebar')
    },

    onStopBackNav (value) {
      this.stopBackNav = value
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/colors';

.icon--hamburger {
  width: 1.875rem;
  height: 1.25rem;
}

.icon--hamburger ::v-deep .svg-fill {
  .c1l & {
    fill: colors.$c1l--green-3;
  }

  .ccc & {
    fill: colors.$ccc--blue-2;
  }

  .ccf & {
    fill: colors.$ccf--blue-1;
  }

  .chs & {
    fill: colors.$chs--green-2;
  }

  .cic & {
    fill: colors.$cic--green-3;
  }

  .esm & {
    fill: colors.$esm--green-2;
  }

  .inl & {
    fill: colors.$inl--green-1;
  }

  .tbs & {
    fill: colors.$tbs--green-3;
  }

  .tmx & {
    fill: colors.$tmx--blue-1;
  }
}

.setup-header {
  max-width: 33.75rem;
  height: 100%;
  max-height: 4rem;
  max-height: calc(constant(safe-area-inset-top) + 4rem); /* iOS 11.0 */
  max-height: calc(env(safe-area-inset-top) + 4rem); /* iOS 11.2 */
  padding: 0.75rem 1rem;
  margin-top: constant(safe-area-inset-top); /* iOS 11.0 */
  margin-top: env(safe-area-inset-top); /* iOS 11.2 */
  white-space: nowrap;

  &.application-progress {
    max-height: 5.625rem;
    max-height: calc(constant(safe-area-inset-top) + 5.625rem); /* iOS 11.0 */
    max-height: calc(env(safe-area-inset-top) + 5.625rem); /* iOS 11.2 */
  }

  &.prevent-click {
    pointer-events: none;
  }

  .button--icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
  }
}

.placeholder {
  width: 2.5rem;
  height: 2.5rem;
}

#backButtonImage {
  width: 0.813rem;
  height: 0.813rem;
  margin-left: 0.188rem;
  border-style: solid;
  border-width: 0 0 3px 3px;
  transform: rotate(45deg);

  .c1l & {
    border-color: colors.$c1l--green-3;
  }

  .ccc & {
    border-color: colors.$ccc--blue-2;
  }

  .ccf & {
    border-color: colors.$ccf--blue-1;
  }

  .chs & {
    border-color: colors.$chs--green-2;
  }

  .cic & {
    border-color: colors.$cic--green-3;
  }

  .esm & {
    border-color: colors.$esm--green-2;
  }

  .inl & {
    border-color: colors.$inl--green-1;
  }

  .tbs & {
    border-color: colors.$tbs--green-3;
  }

  .tmx & {
    border-color: colors.$tmx--blue-1;
  }
}
</style>

<i18n>
  {
    "en": {
      "open_menu": "Open Menu",
      "go_back": "Go Back"
    }
  }
</i18n>
